import {filter, flatMapDeep, includes, isEmpty, pick, some} from 'lodash';
import {RuleGroupType, RuleGroupTypeIC} from 'react-querybuilder';
import {QBOperators} from '../../../../utils/queryBuilder';
import {FieldType} from '../../field-management/custom-field/constant';
import {RelationalGroup} from '../RelationshipGroupPopup';

export const isDateFieldType = (type?: string) =>
  type === 'DateTime' || type === 'Date' || type === 'Time';

export const getDateTimeFieldValue = (
  values: any,
  uiType: FieldType
): string | undefined => {
  if (values?.operator === QBOperators.isIn) {
    return values.periodType === 'Month'
      ? values.periodValue
      : values.periodType;
  }

  if (
    values?.operator === QBOperators.isSetText ||
    values?.operator === QBOperators.isNotSetText
  ) {
    return values.genericValue;
  }

  const startDate = values?.startDate?.format('YYYY-MM-DD');
  const startTime = values?.startTime?.format('HH:mm:ss');
  const endDate = values?.endDate?.format('YYYY-MM-DD');
  const endTime = values?.endTime?.format('HH:mm:ss');

  if (values?.operator === QBOperators.between) {
    switch (uiType) {
      case 'Date':
        return `${startDate} TO ${endDate}`;
      case 'Time':
        return `${startTime} TO ${endTime}`;
      default:
        return `${startDate} ${startTime} TO ${endDate} ${endTime}`;
    }
  }

  switch (uiType) {
    case 'Date':
      return startDate;
    case 'Time':
      return startTime;
    default:
      return `${startDate} ${startTime}`;
  }
};

export const isDateFieldApplyDisabled = (
  fieldUIType?: string,
  values?: any
): boolean => {
  const op = values?.operator;

  const isSetTypeOperator =
    op === QBOperators.isSet ||
    op === QBOperators.isNotSet ||
    op === QBOperators.isSetText ||
    op === QBOperators.isNotSetText;

  if (isSetTypeOperator) return false;

  if (op === QBOperators.isIn) {
    return values?.periodType === 'Month'
      ? isEmpty(values.periodType) || isEmpty(values.periodValue)
      : isEmpty(values?.periodType);
  }

  const isStartDate = isEmpty(values?.startDate);
  const isEndDate = isEmpty(values?.endDate);
  const isStartTime = isEmpty(values?.startTime);
  const isEndTime = isEmpty(values?.endTime);

  if (op === QBOperators.between) {
    if (fieldUIType === 'Date')
      return (
        isStartDate ||
        isEndDate ||
        !values?.startDate.isValid() ||
        !values?.endDate.isValid() ||
        values?.endDate.isBefore(values.startDate, 'day')
      );
    if (fieldUIType === 'Time') return isStartTime || isEndTime;
    return (
      isStartDate ||
      isEndDate ||
      isStartTime ||
      isEndTime ||
      !values?.startDate.isValid() ||
      !values?.endDate.isValid() ||
      values?.endDate.isBefore(values.startDate, 'day')
    );
  }

  if (fieldUIType === 'Date') return isStartDate;
  if (fieldUIType === 'Time') return isStartTime;
  return isStartDate || isStartTime;
};

export const isGeneralFieldApplyDisabled = (values?: any) => {
  const isSetTypeOperator =
    values?.operator === QBOperators.isSet ||
    values?.operator === QBOperators.isNotSet ||
    values?.operator === QBOperators.isSetText ||
    values?.operator === QBOperators.isNotSetText;
  return isEmpty(values?.value) && !isSetTypeOperator;
};

export const isFileFieldApplyDisabled = (values?: any) => {
  const isSetTypeOperator =
    values?.operator === QBOperators.isSet ||
    values?.operator === QBOperators.isNotSet ||
    values?.operator === QBOperators.isSetText ||
    values?.operator === QBOperators.isNotSetText;

  return (
    ((values?.value ?? '') === '*' || isEmpty(values?.value)) &&
    !isSetTypeOperator
  );
};

export const getAllIdsFromQuery = (
  currentQuery: RuleGroupType | RuleGroupTypeIC
): string[] => {
  const nestedIds =
    'rules' in currentQuery
      ? flatMapDeep(currentQuery.rules, (rule) =>
          typeof rule === 'object'
            ? getAllIdsFromQuery(rule as RuleGroupType | RuleGroupTypeIC)
            : []
        )
      : [];

  return [...(currentQuery.id ? [currentQuery.id] : []), ...nestedIds];
};

export const getUpdatedSavedSearchRelationalGroupByIdFromQuery = (
  currentQuery: RuleGroupTypeIC,
  savedSearchRelationalGroupById: Record<string, RelationalGroup[]>
) => {
  const savedSearchIdArray = getAllIdsFromQuery(currentQuery).filter((item) =>
    item.includes('__SAVED_SEARCH__')
  );

  const updatedSavedSearchIdArray = filter(
    Object.keys(savedSearchRelationalGroupById),
    (substring) => {
      return some(savedSearchIdArray, (str) => includes(str, substring));
    }
  );

  return {...pick(savedSearchRelationalGroupById, updatedSavedSearchIdArray)};
};

export const getDisabledRelationshipFieldText = (
  relationalGroupList: RelationalGroup[],
  savedSearchRelationalGroups: RelationalGroup[]
) => {
  const relationalGroupIds = new Set(relationalGroupList?.map((rg) => rg.id));
  const disabledRGFs = savedSearchRelationalGroups
    ?.filter((group) => !relationalGroupIds.has(group.id))
    .map((group) => group.name);
  return disabledRGFs || [];
};

export const isDisabledRGFExistInSavedSearch = (
  relationalGroupList: RelationalGroup[],
  savedSearchRelationalGroups: RelationalGroup[]
) => {
  const disabledRGFList = getDisabledRelationshipFieldText(
    relationalGroupList,
    savedSearchRelationalGroups
  );
  return disabledRGFList.length > 0;
};

export const countRules = (group: any): number => {
  if (!group?.rules) return 0;

  return group.rules.reduce((count: number, item: any) => {
    // Skip combinators ("and", "or")
    if (typeof item === 'string') {
      return count;
    }

    // Nested group
    if (Array.isArray(item.rules)) {
      return count + countRules(item);
    }

    // Actual rule
    return count + 1;
  }, 0);
};
